import { useState, type CSSProperties, type ReactNode } from 'react';
import { Users } from 'lucide-react';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { GROUP_AVATAR, avatarStack, groupInitials, leadingPhoto, type GroupParticipant, type GroupSummary } from '../../../lib/group-chat';
import { useTranslation } from '../../../lib/i18n';
import { MaestroMark } from '../MaestroMark';
import { Avatar } from '../ui';
import styles from './GroupAvatars.module.css';

/**
 * One group participant's mark: a human is the DS `<Avatar>` (profile photo, initials on a missing or failed
 * photo, colour slot by participant id); a Maestro is its OWN avatar carrying the `MaestroMark`, never a badge on
 * its human's photo (design §3.2 R3). iOS `GroupParticipantAvatar` / Android `GroupParticipantAvatar` draw the same.
 */
export function GroupParticipantAvatar({ participant, size }: { participant: GroupParticipant; size: number }) {
  const [failed, setFailed] = useState(false);
  if (participant.kind === 'MAESTRO') return <MaestroMark size={size} />;
  return (
    <Avatar
      size={size}
      initials={groupInitials(participant.displayName)}
      photoUrl={!failed ? participant.avatarUrl : null}
      onPhotoError={() => setFailed(true)}
      color={avatarSlotFor(participant.id)}
    />
  );
}

type Preset = 'row' | 'compact' | 'header';

/**
 * The stacked avatars: up to five of the OTHER participants, then a `+N` pill on the bottom-trailing corner.
 * `row` and `compact` reserve a fixed leading slot so every group row's text starts at the same x;
 * `header` is as wide as the avatars it draws.
 */
export function GroupAvatarStack({ summary, preset }: { summary: GroupSummary; preset: Preset }) {
  const { t } = useTranslation();
  const { shown, plus } = avatarStack(summary);
  const geo = GROUP_AVATAR[preset];
  const drawn = shown.length > 0 ? geo.size + geo.step * (shown.length - 1) : geo.size;
  const width = 'slot' in geo ? geo.slot : drawn;
  return (
    <span className={styles.stack} style={{ width, height: geo.size } as CSSProperties} data-testid="group-avatar-stack">
      {shown.map((p, i) => (
        <span key={`${p.kind}:${p.id}`} className={`${styles.item} ${p.kind === 'MAESTRO' ? styles.maestro : ''}`} style={{ insetInlineStart: i * geo.step, zIndex: i + 1 }}>
          <GroupParticipantAvatar participant={p} size={geo.size} />
        </span>
      ))}
      {plus > 0 && (
        <span className={styles.plus} style={{ zIndex: shown.length + 1 }} role="img" aria-label={t('chat.group.moreMembers', { count: plus })}>
          +{plus}
        </span>
      )}
    </span>
  );
}

/**
 * A group's leading image (Phase 10a, WhatsApp-like): its own photo, a circle the stack's height, in the SAME slot the
 * stack would take (so row text still starts at one x); the avatar stack when it has no photo, or the photo fails.
 * `photoUrl` is what `leadingPhoto` decided (the row's own by default). iOS / Android `GroupLeadingImage` draw the same.
 */
export function GroupLeadingImage({ summary, preset, photoUrl = leadingPhoto(summary) }: { summary: GroupSummary; preset: Preset; photoUrl?: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // An ended group listed for a member its end removed carries no people (VMR #34789): it draws the name's initials.
  const noPeople = summary.participants.length === 0;
  if (!noPeople && (!photoUrl || failedUrl === photoUrl)) return <GroupAvatarStack summary={summary} preset={preset} />;
  const shownPhoto = photoUrl && failedUrl !== photoUrl ? photoUrl : undefined;
  const geo = GROUP_AVATAR[preset];
  const width = 'slot' in geo ? geo.slot : geo.size;
  return (
    <span className={styles.stack} style={{ width, height: geo.size } as CSSProperties} data-testid={shownPhoto ? 'group-photo' : 'group-initials'}>
      <Avatar
        size={geo.size}
        initials={groupInitials(summary.name ?? '')}
        photoUrl={shownPhoto}
        onPhotoError={() => shownPhoto && setFailedUrl(shownPhoto)}
        color={avatarSlotFor(summary.conversationId ?? summary.name ?? '')}
      />
    </span>
  );
}

/** The people icon before a group row's title — it says "group" even when every avatar failed to load. */
export function GroupIndicator({ preset }: { preset: 'row' | 'compact' }) {
  const { t } = useTranslation();
  const size = GROUP_AVATAR[preset].icon;
  return <Users className={styles.indicator} size={size} strokeWidth={2.25} role="img" aria-label={t('chat.group.indicator')} />;
}

export const groupStyles = styles;

/**
 * The leading slot + people icon of a COMPACT group row (web sidebar, mobile recents, search) — GROUP_AVATAR.compact:
 * 20 Ø, step 8, slot 52, 8 gap, the same numbers on iOS and Android. `children` is the row's own text column.
 */
export function GroupCompactRow({ summary, title, children }: { summary: GroupSummary; title: string; children?: ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: GROUP_AVATAR.compact.gap, minWidth: 0, flex: 1, alignSelf: 'stretch' }}>
      <GroupLeadingImage summary={summary} preset="compact" />
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
          <GroupIndicator preset="compact" />
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        </span>
        {children}
      </span>
    </span>
  );
}
